import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../auth/useAuth';
import { managedLocations } from '../../auth/managedLocations';
import { useClientDirectory } from '../../clients/hooks/useClientDirectory';
import { ClientDirectoryClient } from '../../clients/services/clientDirectoryService';
import { rankClients, transformClientName } from '../utils/clientMatch';

export const ClientPicker: React.FC<{ onSelect: (client: ClientDirectoryClient) => void }> = ({
  onSelect,
}) => {
  const { userRole, userMetadata } = useAuth();
  const [hasInteracted, setHasInteracted] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const { clients, loading, error } = useClientDirectory({ enabled: hasInteracted });

  const filteredOptions = useMemo(
    () => rankClients(clients, inputValue, 10),
    [clients, inputValue],
  );

  const disabledReason = useMemo(() => {
    if (!userRole) return 'Loading access…';
    if (userRole === 'MANAGER' && managedLocations(userMetadata).length === 0) return 'No assigned location';
    if (userRole === 'USER' && !userMetadata?.coach_id) return 'No coach ID';
    return null;
  }, [userRole, userMetadata]);

  return (
    <Box sx={{ width: '100%', maxWidth: 560 }}>
      <Autocomplete
        onOpen={() => setHasInteracted(true)}
        openOnFocus
        disabled={Boolean(disabledReason)}
        value={null}
        onChange={(_, value) => {
          if (value) {
            onSelect(value);
          }
          setInputValue('');
        }}
        inputValue={inputValue}
        onInputChange={(_, value) => {
          setHasInteracted(true);
          setInputValue(value);
        }}
        options={filteredOptions}
        filterOptions={(options) => options}
        loading={loading}
        autoHighlight
        clearOnBlur={false}
        getOptionLabel={(option) => `${transformClientName(option.client_name)} (${option.client_id})`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={
          error
            ? error
            : inputValue.trim().length === 0
              ? 'Type to search clients by name, ID or email'
              : 'No matching clients'
        }
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {transformClientName(option.client_name)}{' '}
                <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                  #{option.client_id}
                </Typography>
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                {option.email || 'No email'} • {option.primary_location || 'No location'}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={disabledReason ? disabledReason : 'Search clients by name, ID or email…'}
            disabled={Boolean(disabledReason)}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};
